import { useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Select, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ListPage } from "../../components/ListPage";
import { SiteLabel } from "../../components/SiteLabel";
import { hasAnyGrant } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { useApi } from "../../helpers/useApi";
import { fmtItem } from "../../helpers/format";
import { useItemsQuery, useSitesQuery } from "../../helpers/queries";
import type { ToolCustody } from "../../api-models";

const STATUS_COLORS: Record<string, string> = {
  ISSUED: "blue",
  TRANSFERRED: "geekblue",
  RETURNED: "green",
  LOST: "red",
  DAMAGED: "orange",
};

export default function ToolsListPage() {
  const { user } = useUser();
  const { request, loading } = useApi();
  const { data: items = [] } = useItemsQuery();
  const { data: sites = [] } = useSitesQuery();
  const canWrite = hasAnyGrant(user, "Tools.Write");

  const [open, setOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [form] = Form.useForm();

  // Only TOOL-type items can be taken into custody — the server enforces this too.
  const toolItems = items.filter((i) => i.type === "TOOL");

  async function issue() {
    const values = await form.validateFields();
    await request({ method: "POST", url: "/tools", data: values });
    message.success("Tool issued into custody.");
    setOpen(false);
    form.resetFields();
    setReloadKey((k) => k + 1);
  }

  return (
    <>
      <ListPage<ToolCustody>
        key={reloadKey}
        title="Tool Custody"
        endpoint="/tools"
        searchPlaceholder="Search item or asset id..."
        linkTo={(r) => `/tools/${r.id}`}
        actions={
          canWrite && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)} data-tour="tools-issue">
              Issue tool
            </Button>
          )
        }
        columns={[
          { title: "Code", render: (_, r) => r.item?.code },
          { title: "Item", render: (_, r) => r.item?.name },
          { title: "Asset ID", dataIndex: "serializedAssetId", sorter: true, render: (v) => v || "—" },
          { title: "Held at", render: (_, r) => <SiteLabel site={r.site} fallback="Central" /> },
          { title: "Qty", dataIndex: "qty", sorter: true },
          { title: "Condition", dataIndex: "condition", sorter: true, render: (v) => v || "—" },
          { title: "Status", dataIndex: "status", sorter: true, render: (v: string) => <Tag color={STATUS_COLORS[v]}>{v}</Tag> },
        ]}
        renderCard={(r) => (
          <div>
            <strong>{fmtItem(r.item)}</strong>
            <div><SiteLabel site={r.site} fallback="Central" /></div>
            <Tag color={STATUS_COLORS[r.status]}>{r.status}</Tag>
          </div>
        )}
      />

      <Modal title="Issue tool into custody" open={open} onCancel={() => setOpen(false)} onOk={issue} confirmLoading={loading} destroyOnHidden>
        <Form form={form} layout="vertical" initialValues={{ qty: 1 }}>
          <Form.Item name="itemId" label="Tool" rules={[{ required: true }]} extra="Only items classified as TOOL can be taken into custody.">
            <Select
              showSearch
              optionFilterProp="label"
              options={toolItems.map((i) => ({ value: i.id, label: `${i.code} — ${i.name}` }))}
              notFoundContent="No TOOL-type items in the Item Master yet."
            />
          </Form.Item>
          <Form.Item name="siteId" label="Site" extra="Leave empty to hold the tool at Central.">
            <Select allowClear options={sites.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="qty" label="Quantity" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="serializedAssetId" label="Asset / serial number">
            <Input placeholder="For serialised assets" />
          </Form.Item>
          <Form.Item name="condition" label="Condition at issue">
            <Input placeholder="e.g. Good, minor wear" />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
